<template>
  <div class="drop-file" >
    <div>
      <div
        @dragover="dragover = true"
        @dragenter="dragover = true"
        @dragleave="dragover = false"
        @dragend="dragover = false"
        @mouseout="dragover = false"
        @drop="parseFiles($event)"
        :class="{
          dragover: dragover,
          'has-files': files.length
        }"
        ref="dropBox"
        class="drop-box" >
        <div v-if="browserSupportsDrop" class="composed-icon" >
          <v-icon>arrow_downward</v-icon>
          <v-icon>space_bar</v-icon>
        </div>
        <span class="text" >
          <button
            @click="$refs.input.click()" >
            {{$tf('Choose a file')}}
          </button>
          {{browserSupportsDrop ? ' ' + $tf('or drag it here') : null}}
        </span>

        <v-list :style="{
            display: files.length ? 'block' : 'none',
            padding: 0
          }" >
          <transition-group name="list" tag="div" class="transition-container" >
            <v-list-tile
              v-for="(file, index) in files"
              :key="file.lastModified + file.name"
              class="list-item"
              avatar >
              <v-list-tile-avatar>
                <v-icon :style="{
                  backgroundColor: colorfromName(iconFromType(file.type)),
                  color: 'white'
                }" >
                {{iconFromType(file.type)}}
              </v-icon>
              </v-list-tile-avatar>
              <v-list-tile-content>
                <v-list-tile-title>
                {{file.name}} | {{formatBytes(file.size)}}
                </v-list-tile-title>
              </v-list-tile-content>
              <v-list-tile-action>
                <v-btn
                  @click="removeFile(index)"
                  ripple
                  icon>
                  <v-icon class="error--text">cancel</v-icon>
                </v-btn>
              </v-list-tile-action>
            </v-list-tile>
          </transition-group>
        </v-list>
      </div>
      <input
        :value="file"
        :accept="accept"
        :multiple="multiple"
        @input="parseFiles($event)"
        @change="parseFiles($event)"
        ref="input"
        type="file"
        style="display: none" />
    </div>
  </div>
</template>

<script>
  import Color from '~/helpers/color.js'
  import isValidType from '~/helpers/MIMEType.js'
  import {IconFromType} from '~/helpers/icon.js'

  export default {
    name: 'drop-file',
    props: {
      accept: {
        type: String,
        default: '*'
      },
      multiple: {
        type: Boolean,
        default: true
      },
      maxFileSize: { // Maximum size for each file in Mb, 0 = unlimited
        type: Number,
        default: 0
      }
    },
    data () {
      return {
        dragover: false,
        file: null,
        files: [],
        lastEventTime: 0
      }
    },
    computed: {
      form () {
        const data = new FormData()
        this.files.forEach(file => {
          data.append('files', file, file.name)
        })
        return data
      },
      browserSupportsDrop () {
        var div = document.createElement('div')
        return (('draggable' in div) || ('ondragstart' in div && 'ondrop' in div)) && 'FormData' in window && 'FileReader' in window
      }
    },
    mounted () {
      const events = 'drag dragstart dragend dragover dragenter dragleave drop'
      events.split(' ').forEach(eventName => {
        this.$refs.dropBox.addEventListener(eventName, function (e) {
          e.preventDefault()
          e.stopPropagation()
        })
      })
    },
    methods: {
      colorfromName: Color.fromName,
      removeFile (index) {
        this.files.splice(index, 1)
      },
      parseFiles (event) {
        const files = event.target.files || event.dataTransfer.files
        this.$emit('rawFilesChanged', event.dataTransfer || event.target.files)
        if (this.multiple) {
          for (let key in files) {
            const file = files[key]

            if (!(this.isValidMIMEType(file.type) && this.isValidFileSize(file.size))) {
              if (!this.isValidFileSize(file.size) && event.timeStamp - this.lastEventTime > 100) {
                this.lastEventTime = event.timeStamp
                this.$store.dispatch('feedback/addSnack', {
                  text: file.name + ' ' + this.$tf('exceeds the limit of') + ' ' + this.maxFileSize + 'MB',
                  type: 'error'
                })
              }
              if (!this.isValidMIMEType(file.type) && event.timeStamp - this.lastEventTime > 100) {
                this.lastEventTime = event.timeStamp
                this.$store.dispatch('feedback/addSnack', {
                  text: file.name + ' ' + this.$tf('is an invalid type of file'),
                  type: 'error'
                })
              }
              continue
            }

            if (Object.prototype.toString.call(file) === '[object File]') {
              this.files.push(files[key])
            }
          }
        } else {
          const file = files[files.length - 1]
          if (!this.isValidFileSize(file.size) && event.timeStamp - this.lastEventTime > 100) {
            this.lastEventTime = event.timeStamp
            this.$store.dispatch('feedback/addSnack', {
              text: this.$tf('This file exceeds the limit of') + ' ' + this.maxFileSize + 'MB',
              type: 'error'
            })
          }
          if (!this.isValidMIMEType(file.type) && event.timeStamp - this.lastEventTime > 100) {
            this.lastEventTime = event.timeStamp
            this.$store.dispatch('feedback/addSnack', {
              text: this.$tf('Invalid type of file'),
              type: 'error'
            })
          }

          if (this.isValidMIMEType(file.type) && this.isValidFileSize(file.size)) {
            this.files = [file]
          }
        }
      },
      formatBytes (bytes) {
        const thresh = 1024
        if (Math.abs(bytes) < thresh) {
          return bytes + ' B'
        }
        const units = ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
        let u = -1
        do {
          bytes /= thresh
          ++u
        } while (Math.abs(bytes) >= thresh && u < units.length - 1)
        return bytes.toFixed(1) + ' ' + units[u]
      },
      iconFromType (MIMEType) {
        const type = MIMEType.split('/')[0]
        const specification = MIMEType.split('/')[1]
        return specification === 'pdf' ? 'picture_as_pdf' : IconFromType(type, 'insert_drive_file')
      },
      isValidMIMEType (MIMEType) {
        return isValidType(this.accept, MIMEType)
      },
      isValidFileSize (size) {
        if (this.maxFileSize === 0) {
          return true
        }

        return size <= this.maxFileSize * 1048576
      }
    },
    watch: {
      form (form) {
        this.$emit('filesChanged', form)
      }
    }
  }
</script>

<style lang="scss" scoped >
 .drop-file {
    background-color:  #c8dadf;
    padding: 1rem;

    .text {
      margin: 2rem 4rem;
      display: block;
    }

    button {
      background-color: transparent;
      color: rgba(0,0,0,0.87);
      font-size: 1.5rem;
      font-weight: bold;
      margin: 0;

      &:focus,
      &:active {
        outline: 0;
      }
    }
  }

  .drop-box {
    padding: 0;
    border: 2px dashed #92b0b3;
    text-align: center;
    font-size: 1.5rem;
    background-color: rgba(255, 255, 255, 0);
    transition: .25s ease-out;

    &.dragover {
      background-color: rgba(70, 169, 212, 0.2);
    }
  }


  .composed-icon {
    margin: 1rem 0 2.5rem 0;
    display: block;
    transition: .25s ease-out;

    i {
      display: block;
      color: rgba(0,0,0,0.5);

      &:first-child {
        font-size: 4rem;
        font-weight: bold;
        line-height: 4rem;
        transition: .15s ease-out;

        .dragover & {
          padding-top: 1rem;
          line-height: 3rem;
        }
      }
      &:last-child {
        font-size: 6rem;
        line-height: 0rem;
      }
    }
  }

  .list {
    background-color: rgba(255, 255, 255, 1);

    .transition-container {
      position: relative;
      display: block;
      padding: 0px;
      max-height: 250px;
      overflow-y: auto;
      overflow-x: hidden;
      width: 100%;

      .list-item {
        transition: all .5s ease-out;
        display: block;
        background: transparent;
      }
      .list-enter, .list-leave-to {
        opacity: 0;
        transform: translateX(30px);
      }

      .list-leave-active {
        position: absolute;
        left: 0;
        right: 0;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/common/DropFile.vue